const SCROLL_TOLERANCE = 3;
const PROGRAMMATIC_SCROLL_DELAY = 350;
const USER_SCROLL_DELAY = 50;
const supportsScrollEnd = 'onscrollend' in window;
const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const getMaxScroll = (carousel) => Math.max(0, carousel.scrollWidth - carousel.clientWidth);

const getPaddingLeft = (carousel) => parseFloat(window.getComputedStyle(carousel).paddingLeft) || 0;

const getLogoScrollTarget = (logo, paddingLeft, maxScroll) =>
  Math.min(Math.max(0, logo.offsetLeft - paddingLeft), maxScroll);

const getPageScrollPositions = (carousel) => {
  const logos = [...carousel.querySelectorAll('.cotopaxi-logo-wall__logo')];
  const maxScroll = getMaxScroll(carousel);
  const paddingLeft = getPaddingLeft(carousel);

  if (maxScroll <= SCROLL_TOLERANCE || !logos.length) {
    return [0];
  }

  const positions = [0];
  let pageStart = 0;

  while (pageStart < maxScroll - SCROLL_TOLERANCE) {
    const viewportEnd = pageStart + carousel.clientWidth;
    const nextLogo = logos.find((logo) => logo.offsetLeft >= viewportEnd - SCROLL_TOLERANCE);

    if (!nextLogo) {
      if (positions[positions.length - 1] < maxScroll - SCROLL_TOLERANCE) {
        positions.push(maxScroll);
      }
      break;
    }

    const nextPosition = getLogoScrollTarget(nextLogo, paddingLeft, maxScroll);

    if (nextPosition <= positions[positions.length - 1] + SCROLL_TOLERANCE) {
      if (positions[positions.length - 1] < maxScroll - SCROLL_TOLERANCE) {
        positions.push(maxScroll);
      }
      break;
    }

    positions.push(nextPosition);
    pageStart = nextPosition;
  }

  return positions;
};

const getCurrentPage = (carousel, positions) => {
  const scrollPosition = carousel.scrollLeft;

  for (let index = 1; index < positions.length; index += 1) {
    const midpoint = positions[index - 1] + (positions[index] - positions[index - 1]) / 2;

    if (scrollPosition < midpoint) {
      return index - 1;
    }
  }

  return positions.length - 1;
};

export default (function () {
  return {
    init() {
      document.querySelectorAll('[data-component="cotopaxi-logo-wall"]').forEach((root) => {
        const carousel = root.querySelector('.cotopaxi-logo-wall__carousel');
        const pagination = root.querySelector('.cotopaxi-logo-wall__pagination');
        const status = root.querySelector('.cotopaxi-logo-wall__status');
        const prevButton = root.querySelector('.cotopaxi-logo-wall__control--prev');
        const nextButton = root.querySelector('.cotopaxi-logo-wall__control--next');
        const logos = carousel ? [...carousel.querySelectorAll('.cotopaxi-logo-wall__logo')] : [];

        if (!carousel || !pagination || !logos.length) {
          return;
        }

        let pagePositions = [0];
        let currentPage = 0;
        let paginationButtons = [];
        let scrollTimer;
        let isProgrammaticScroll = false;

        const updateControls = () => {
          const atStart = currentPage <= 0;
          const atEnd = currentPage >= pagePositions.length - 1;

          if (prevButton) {
            prevButton.disabled = atStart;
          }

          if (nextButton) {
            nextButton.disabled = atEnd;
          }

          paginationButtons.forEach((button, index) => {
            button.classList.toggle('is-active', index === currentPage);
            button.setAttribute('aria-current', index === currentPage ? 'true' : 'false');
          });

          if (status) {
            status.textContent = pagePositions.length > 1
              ? `Showing logo set ${currentPage + 1} of ${pagePositions.length}`
              : '';
          }
        };

        const scrollToPage = (pageIndex) => {
          const target = pagePositions[pageIndex];

          if (target === undefined) {
            return;
          }

          isProgrammaticScroll = true;
          carousel.scrollTo({
            left: target,
            behavior: prefersReducedMotion ? 'auto' : 'smooth',
          });
        };

        const goToPage = (pageIndex) => {
          currentPage = pageIndex;
          scrollToPage(currentPage);
          updateControls();
        };

        const syncPageFromScroll = () => {
          currentPage = getCurrentPage(carousel, pagePositions);
          isProgrammaticScroll = false;
          updateControls();
        };

        const renderPagination = () => {
          pagination.innerHTML = '';
          paginationButtons = pagePositions.map((_, index) => {
            const button = document.createElement('button');
            button.type = 'button';
            button.className = 'cotopaxi-logo-wall__pagination-button';
            button.setAttribute('aria-label', `Show logo set ${index + 1}`);
            button.addEventListener('click', () => goToPage(index));
            pagination.appendChild(button);
            return button;
          });

          updateControls();
        };

        const refresh = () => {
          pagePositions = getPageScrollPositions(carousel);
          currentPage = Math.min(getCurrentPage(carousel, pagePositions), pagePositions.length - 1);
          renderPagination();
        };

        const onScroll = () => {
          window.clearTimeout(scrollTimer);
          scrollTimer = window.setTimeout(
            syncPageFromScroll,
            isProgrammaticScroll ? PROGRAMMATIC_SCROLL_DELAY : USER_SCROLL_DELAY,
          );
        };

        if (prevButton) {
          prevButton.addEventListener('click', () => {
            goToPage(Math.max(0, currentPage - 1));
          });
        }

        if (nextButton) {
          nextButton.addEventListener('click', () => {
            goToPage(Math.min(pagePositions.length - 1, currentPage + 1));
          });
        }

        carousel.addEventListener('pointerdown', () => {
          isProgrammaticScroll = false;
        }, { passive: true });

        if (supportsScrollEnd) {
          carousel.addEventListener('scrollend', syncPageFromScroll);
        } else {
          carousel.addEventListener('scroll', onScroll, { passive: true });
        }

        if (typeof ResizeObserver !== 'undefined') {
          const resizeObserver = new ResizeObserver(refresh);
          resizeObserver.observe(carousel);
        } else {
          window.addEventListener('resize', refresh);
        }

        const pendingImages = logos
          .map((logo) => logo.querySelector('img'))
          .filter(Boolean)
          .filter((image) => !image.complete);

        if (pendingImages.length) {
          Promise.all(
            pendingImages.map(
              (image) =>
                new Promise((resolve) => {
                  image.addEventListener('load', resolve, { once: true });
                  image.addEventListener('error', resolve, { once: true });
                }),
            ),
          ).then(refresh);
        }

        refresh();
      });
    },
  };
})();
